import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import Layout from '../components/Layout';
import PageTabBar from '../components/PageTabBar';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Printer, Settings, Store, Warehouse, FileText, Send, AlertTriangle } from 'lucide-react';
import { STOCK_TABS, ETIQUETTES_SUB_TABS } from './_stockTabs';
import JsBarcode from 'jsbarcode';

const BarcodePreview = ({ value }) => {
  const svgRef = useRef(null);
  const { t } = useLanguage();
  useEffect(() => {
    if (svgRef.current && value) {
      try {
        JsBarcode(svgRef.current, value, {
          format: 'EAN13',
          width: 1.5,
          height: 40,
          displayValue: true,
          fontSize: 12,
          background: 'transparent',
          lineColor: '#e2e8f0',
          margin: 4,
        });
      } catch {
        try {
          JsBarcode(svgRef.current, value, {
            format: 'CODE128',
            width: 1.5,
            height: 40,
            displayValue: true,
            fontSize: 12,
            background: 'transparent',
            lineColor: '#e2e8f0',
            margin: 4,
          });
        } catch {
          // fallback
        }
      }
    }
  }, [value]);

  if (!value) return <div className="text-gray-500 text-xs italic">{t('labels.barcodeAuto')}</div>;
  return <svg ref={svgRef} />;
};

import { useNavigate, useLocation } from 'react-router-dom';

const EtiquettesPage = () => {
  const { api } = useAuth();
  const { t, tError, formatCurrency, formatNumber } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState({});
  const [generating, setGenerating] = useState(false);
  const [stockFilter, setStockFilter] = useState('boutique');
  // Feature 4 (2026-05) — Bixolon batch printing (50×30mm) vs legacy A4 planche.
  const [labelMode, setLabelMode] = useState('bixolon'); // 'bixolon' | 'a4'
  const [confirmModal, setConfirmModal] = useState(null); // {totalLabels, action} or null

  const fetchStock = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/stock');
      const allItems = response.data || [];
      setItems(allItems);
      // Pre-select articles from inventory page
      if (location.state?.preselected) {
        const pre = {};
        location.state.preselected.forEach(p => {
          const found = allItems.find(i => i.id === p.id);
          if (found) pre[p.id] = p.qty || 1;
        });
        if (Object.keys(pre).length > 0) setSelected(pre);
        if (location.state.stockFilter) setStockFilter(location.state.stockFilter);
      }
    } catch (error) {
      console.error('Erreur stock:', error);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchStock();
  }, [fetchStock]);

  const filteredItems = useMemo(() => {
    if (!search) return items;
    const term = search.toLowerCase();
    return items.filter(item =>
      (item.designation || '').toLowerCase().includes(term) ||
      (item.reference || '').toLowerCase().includes(term)
    );
  }, [items, search]);

  const getStockQty = (item) => {
    if (stockFilter === 'depot') return item.quantite_depot || 0;
    return item.quantite || 0;
  };

  const toggleSelect = (itemId) => {
    setSelected((prev) => {
      if (prev[itemId]) {
        const copy = { ...prev };
        delete copy[itemId];
        return copy;
      }
      const item = items.find(i => i.id === itemId);
      const qty = item ? getStockQty(item) : 1;
      return { ...prev, [itemId]: Math.max(1, qty) };
    });
  };

  const updateQty = (itemId, value) => {
    const qty = Math.max(1, parseInt(value, 10) || 1);
    setSelected((prev) => ({ ...prev, [itemId]: qty }));
  };

  const toggleSelectAll = () => {
    if (Object.keys(selected).length === filteredItems.length) {
      setSelected({});
      return;
    }
    const next = {};
    filteredItems.forEach(item => {
      const qty = getStockQty(item);
      next[item.id] = selected[item.id] || Math.max(1, qty);
    });
    setSelected(next);
  };

  const openBlobForPrint = (blob) => {
    const url = window.URL.createObjectURL(blob);
    const printWindow = window.open(url, '_blank', 'width=900,height=700');
    if (!printWindow) {
      alert(t('labels.allowPopups'));
      return;
    }
    const triggerPrint = () => {
      printWindow.focus();
      printWindow.print();
    };
    printWindow.addEventListener('load', triggerPrint);
    setTimeout(triggerPrint, 1200);
    setTimeout(() => window.URL.revokeObjectURL(url), 8000);
  };

  const handleGenerate = async () => {
    const payloadItems = Object.entries(selected).map(([id, quantity]) => ({
      product_id: id,
      quantity
    }));
    if (!payloadItems.length) {
      alert(t('labels.selectAtLeastOne'));
      return;
    }
    setGenerating(true);
    try {
      const response = await api.post('/stock/labels', { items: payloadItems }, { responseType: 'blob' });
      openBlobForPrint(response.data);
      fetchStock();
    } catch (error) {
      alert(tError(error.response?.data?.detail) || t('labels.errorGenerate'));
    } finally {
      setGenerating(false);
    }
  };

  // ───── Feature 4 (2026-05) — Bixolon batch (50×30mm) ─────
  const buildPayload = () => Object.entries(selected).map(([id, quantity]) => ({
    product_id: id,
    quantity,
  }));

  const doBatch = async (action) => {
    const payloadItems = buildPayload();
    if (!payloadItems.length) { alert(t('labels.selectAtLeastOne')); return; }
    setGenerating(true);
    try {
      if (action === 'pdf') {
        const response = await api.post('/stock/labels/batch?output=pdf',
          { items: payloadItems, format: '50x30mm' },
          { responseType: 'blob', timeout: 5 * 60 * 1000 });
        const noEan = parseInt(response.headers['x-labels-no-ean'] || '0', 10);
        const total = parseInt(response.headers['x-labels-total'] || '0', 10);
        if (noEan > 0) {
          // Non-blocking warning toast (alert is fine for MVP)
          setTimeout(() => alert(t('labels.infoNoEan', { count: noEan, total })), 300);
        }
        openBlobForPrint(response.data);
      } else if (action === 'job') {
        const response = await api.post('/stock/labels/batch?output=job',
          { items: payloadItems, format: '50x30mm' },
          { timeout: 5 * 60 * 1000 });
        const { labels_count, job_id, no_ean = [] } = response.data || {};
        let msg = t('labels.jobCreated', { count: labels_count, id: job_id?.slice(0, 8) || '' });
        if (no_ean.length) msg += t('labels.jobNoEan', { count: no_ean.length });
        alert(msg);
      }
      fetchStock();
    } catch (error) {
      alert(tError(error.response?.data?.detail) || t('labels.errorBatch'));
    } finally {
      setGenerating(false);
    }
  };

  // Confirm-modal guard at 500 / 5000 thresholds (UX anti-misclick).
  const handleBatch = (action) => {
    if (totalLabels === 0) { alert(t('labels.selectAtLeastOne')); return; }
    if (totalLabels > 500) {
      setConfirmModal({ totalLabels, action });
      return;
    }
    doBatch(action);
  };

  const confirmModalAccept = () => {
    if (!confirmModal) return;
    const { action } = confirmModal;
    setConfirmModal(null);
    doBatch(action);
  };

  const selectedCount = Object.keys(selected).length;
  const totalLabels = Object.values(selected).reduce((a, b) => a + b, 0);

  const previewItem = useMemo(() => {
    const selectedId = Object.keys(selected)[0];
    return items.find(item => item.id === selectedId) || filteredItems[0];
  }, [selected, items, filteredItems]);

  const GenerateButton = ({ className = '' }) => (
    <button
      onClick={handleGenerate}
      disabled={generating || selectedCount === 0}
      className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold transition-all ${
        selectedCount > 0
          ? 'bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/30 border border-emerald-500/30'
          : 'bg-gray-700 text-gray-500 cursor-not-allowed border border-white/5'
      } ${className}`}
      data-testid="etiquettes-generate"
    >
      <Printer className="w-4 h-4" />
      {generating ? t('labels.generating') : t('labels.generatePdf', { count: totalLabels })}
    </button>
  );

  // Feature 4 — Bixolon batch buttons (PDF preview + send-to-agent).
  const BatchButtons = ({ className = '' }) => (
    <div className={`flex flex-wrap items-center gap-2 ${className}`}>
      <button
        onClick={() => handleBatch('pdf')}
        disabled={generating || selectedCount === 0}
        className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-semibold text-sm transition-all ${
          selectedCount > 0
            ? 'bg-sky-500/20 text-sky-300 hover:bg-sky-500/30 border border-sky-500/30'
            : 'bg-gray-700 text-gray-500 cursor-not-allowed border border-white/5'
        }`}
        data-testid="etiquettes-batch-pdf"
      >
        <FileText className="w-4 h-4" />
        {generating ? t('labels.generating') : t('labels.previewPdf', { count: totalLabels })}
      </button>
      <button
        onClick={() => handleBatch('job')}
        disabled={generating || selectedCount === 0}
        className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-semibold text-sm transition-all ${
          selectedCount > 0
            ? 'bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/30 border border-emerald-500/30'
            : 'bg-gray-700 text-gray-500 cursor-not-allowed border border-white/5'
        }`}
        data-testid="etiquettes-batch-job"
      >
        <Send className="w-4 h-4" />
        {t('labels.printBixolon', { count: totalLabels })}
      </button>
      <button
        onClick={() => setSelected({})}
        disabled={selectedCount === 0}
        className="px-3 py-2.5 rounded-xl text-sm font-semibold bg-white/5 hover:bg-white/10 text-gray-300 disabled:opacity-40 disabled:cursor-not-allowed"
        data-testid="etiquettes-clear"
      >
        {t('labels.clear')}
      </button>
    </div>
  );

  // Format dropdown — Feature 4 vs legacy A4 planche.
  const FormatSelector = () => (
    <div className="flex items-center gap-2">
      <label className="text-xs uppercase tracking-wider text-gray-400 font-semibold">{t('labels.format')}</label>
      <select
        value={labelMode}
        onChange={(e) => setLabelMode(e.target.value)}
        className="input-dark py-2 px-3 text-sm"
        data-testid="etiquettes-format-select"
      >
        <option value="bixolon">{t('labels.formatBixolon')}</option>
        <option value="a4">{t('labels.formatA4')}</option>
      </select>
    </div>
  );

  return (
    <Layout>
      <div className="max-w-7xl mx-auto fade-in" data-testid="etiquettes-page">
        <PageTabBar tabs={STOCK_TABS} activeColor="rose" />
        <PageTabBar tabs={ETIQUETTES_SUB_TABS} activeColor="emerald" />
        {/* Header with generate button */}
        <div className="flex items-center justify-between gap-3 mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-emerald-500/10 text-emerald-400">
              <Printer className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-xl md:text-2xl font-bold text-white">{t('labels.pageTitle')}</h2>
              <p className="text-sm text-gray-400">{t('labels.pageSubtitle')}</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <FormatSelector />
            <button
              onClick={() => navigate('/etiquettes/config')}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl font-semibold text-sm bg-amber-500/10 text-amber-300 hover:bg-amber-500/20 border border-amber-500/20 transition-all"
              data-testid="etiquettes-config-btn"
            >
              <Settings className="w-4 h-4" /> {t('labels.config')}
            </button>
            {labelMode === 'a4' ? <GenerateButton /> : <BatchButtons />}
          </div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-[1.6fr_1fr] gap-6">
          {/* Product list - scrollable */}
          <div className="glass p-6">
            <div className="flex flex-wrap gap-3 items-center justify-between mb-4">
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t('labels.searchPlaceholder')}
                className="input-dark w-full md:w-72 py-2"
                data-testid="etiquettes-search"
              />
              <div className="flex items-center gap-2">
                <div className="flex rounded-xl overflow-hidden border border-white/10">
                  <button onClick={() => { setStockFilter('boutique'); setSelected({}); }}
                    className={`flex items-center gap-1.5 px-3 py-2 text-xs font-semibold transition-colors ${stockFilter === 'boutique' ? 'bg-green-500/20 text-green-400' : 'bg-white/5 text-gray-500 hover:bg-white/10'}`}
                    data-testid="filter-boutique">
                    <Store className="w-3.5 h-3.5" /> {t('labels.boutique')}
                  </button>
                  <button onClick={() => { setStockFilter('depot'); setSelected({}); }}
                    className={`flex items-center gap-1.5 px-3 py-2 text-xs font-semibold transition-colors ${stockFilter === 'depot' ? 'bg-purple-500/20 text-purple-400' : 'bg-white/5 text-gray-500 hover:bg-white/10'}`}
                    data-testid="filter-depot">
                    <Warehouse className="w-3.5 h-3.5" /> {t('labels.depot')}
                  </button>
                </div>
                <button
                  onClick={toggleSelectAll}
                  className="px-4 py-2 rounded-xl bg-white/10 hover:bg-white/20 text-sm font-semibold"
                  data-testid="etiquettes-select-all"
                >
                  {selectedCount === filteredItems.length ? t('labels.deselectAll') : t('labels.selectAll')}
                </button>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full table-dark-custom text-sm">
                <thead>
                  <tr>
                    <th></th>
                    <th>{t('labels.colProduct')}</th>
                    <th>{t('labels.colPrice')}</th>
                    <th>EAN13</th>
                    <th><span className="flex items-center gap-1"><Store className="w-3 h-3 text-green-400" /> {t('labels.colBoutiqueShort')}</span></th>
                    <th><span className="flex items-center gap-1"><Warehouse className="w-3 h-3 text-purple-400" /> {t('labels.depot')}</span></th>
                    <th>{t('labels.colQtyToPrint')}</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="6" className="text-center py-8">
                        <div className="loading-spinner mx-auto"></div>
                      </td>
                    </tr>
                  ) : filteredItems.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="text-center py-8 text-gray-400">
                        {t('labels.noProduct')}
                      </td>
                    </tr>
                  ) : (
                    filteredItems.map((item) => {
                      const qBout = item.quantite || 0;
                      const qDepot = item.quantite_depot || 0;
                      const activeQty = stockFilter === 'depot' ? qDepot : qBout;
                      return (
                      <tr key={item.id} data-testid={`etiquette-row-${item.id}`}>
                        <td>
                          <input
                            type="checkbox"
                            checked={!!selected[item.id]}
                            onChange={() => toggleSelect(item.id)}
                            className="w-4 h-4"
                          />
                        </td>
                        <td>
                          <div className="font-semibold">{item.designation}</div>
                          <div className="text-xs text-gray-400">{item.reference}</div>
                        </td>
                        <td className="font-bold text-emerald-300">{item.prix_vente != null ? formatCurrency(item.prix_vente) : '—'}</td>
                        <td className="font-mono text-xs text-gray-300">{item.ean || <span className="text-yellow-400">{t('labels.autoShort')}</span>}</td>
                        <td>
                          <button onClick={() => { setStockFilter('boutique'); setSelected(prev => ({ ...prev, [item.id]: Math.max(1, qBout) })); }}
                            className={`font-semibold px-2 py-1 rounded-lg transition-colors cursor-pointer ${selected[item.id] && stockFilter === 'boutique' ? 'bg-green-500/20 text-green-400 ring-1 ring-green-500/50' : 'text-green-400/60 hover:bg-green-500/10 hover:text-green-400'}`}
                            data-testid={`pick-boutique-${item.id}`}>
                            {qBout}
                          </button>
                        </td>
                        <td>
                          <button onClick={() => { setStockFilter('depot'); setSelected(prev => ({ ...prev, [item.id]: Math.max(1, qDepot) })); }}
                            className={`font-semibold px-2 py-1 rounded-lg transition-colors cursor-pointer ${selected[item.id] && stockFilter === 'depot' ? 'bg-purple-500/20 text-purple-400 ring-1 ring-purple-500/50' : 'text-purple-400/60 hover:bg-purple-500/10 hover:text-purple-400'}`}
                            data-testid={`pick-depot-${item.id}`}>
                            {qDepot}
                          </button>
                        </td>
                        <td>
                          <input
                            type="number"
                            min="1"
                            value={selected[item.id] || ''}
                            onChange={(e) => updateQty(item.id, e.target.value)}
                            className="input-dark w-20 py-1 text-center"
                            disabled={!selected[item.id]}
                            placeholder={activeQty > 0 ? String(activeQty) : '0'}
                          />
                        </td>
                      </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Preview - STICKY */}
          <div className="sticky self-start" style={{ top: '5rem' }} data-testid="etiquettes-preview">
            <div className="glass p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-emerald-300">{t('labels.previewTitle')}</h3>
              </div>
              <div className="mb-4">
                {labelMode === 'a4'
                  ? <GenerateButton className="w-full justify-center" />
                  : <BatchButtons className="justify-center" />}
              </div>

              {previewItem ? (
                <div className="space-y-4">
                  {/* Simulated label */}
                  <div className="bg-white rounded-lg p-4 text-black max-w-[280px] mx-auto" style={{border: '2px dashed #ccc'}}>
                    <div className="text-center">
                      <div className="font-bold text-xs uppercase tracking-wide text-gray-800 truncate">
                        R.E.Mobiles
                      </div>
                      <div className="mt-2">
                        <BarcodePreview value={previewItem.ean} />
                      </div>
                      <div className="font-bold text-sm mt-1 truncate text-black">
                        {previewItem.designation}
                      </div>
                      <div className="font-bold text-lg mt-1 text-red-600">
                        {previewItem.prix_vente != null ? formatCurrency(previewItem.prix_vente) : '—'}
                      </div>
                      <div className="text-[10px] text-gray-500 mt-1">
                        {t('labels.refLabel')} {previewItem.reference || '--'}
                      </div>
                    </div>
                  </div>

                  {/* Selection summary */}
                  {selectedCount > 0 && (
                    <div className="p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/30 text-sm space-y-1">
                      <div className="flex justify-between text-emerald-300">
                        <span>{t('labels.selectedProducts')}</span>
                        <span className="font-bold">{selectedCount}</span>
                      </div>
                      <div className="flex justify-between text-emerald-300">
                        <span>{t('labels.totalLabels')}</span>
                        <span className="font-bold">{totalLabels}</span>
                      </div>
                    </div>
                  )}

                  {/* Product info */}
                  <div className="text-xs text-gray-400 space-y-1 border-t border-white/10 pt-3">
                    <div className="flex justify-between">
                      <span>{t('labels.colProduct')}</span>
                      <span className="text-white font-semibold truncate ml-2 max-w-[60%] text-right">{previewItem.designation}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>{t('labels.colPrice')}</span>
                      <span className="text-emerald-300 font-bold">{previewItem.prix_vente != null ? formatCurrency(previewItem.prix_vente) : '—'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>EAN13</span>
                      <span className="font-mono text-gray-300">{previewItem.ean || <span className="text-yellow-400">{t('labels.autoGenAtPdf')}</span>}</span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-gray-400 text-center py-8">{t('labels.selectToPreview')}</div>
              )}
            </div>
          </div>
        </div>

        {/* Feature 4 — Confirm modal for large batches (>500 / >5000) */}
        {confirmModal && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
            data-testid="batch-confirm-modal"
            onClick={() => setConfirmModal(null)}
          >
            <div
              className="bg-gray-900 border border-amber-500/30 rounded-2xl max-w-md w-full p-6 shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-start gap-3 mb-4">
                <AlertTriangle className={`w-6 h-6 mt-1 ${confirmModal.totalLabels > 5000 ? 'text-red-400' : 'text-amber-400'}`} />
                <div>
                  <h3 className={`text-lg font-bold ${confirmModal.totalLabels > 5000 ? 'text-red-300' : 'text-amber-300'}`}>
                    {confirmModal.totalLabels > 5000 ? t('labels.batchVeryLarge') : t('labels.batchConfirmTitle')}
                  </h3>
                  <p className="text-sm text-gray-300 mt-2">
                    {t('labels.confirmBodyPrefix')} <span className="font-bold text-white">{confirmModal.totalLabels}</span> {t('labels.confirmBodySuffix')}
                  </p>
                  {confirmModal.totalLabels > 5000 && (
                    <div className="mt-3 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-xs text-red-200 space-y-1">
                      <div>{t('labels.estRoll', { meters: formatNumber(confirmModal.totalLabels * 0.030, { minimumFractionDigits: 1, maximumFractionDigits: 1 }) })}</div>
                      <div>{t('labels.estTime', { minutes: Math.ceil(confirmModal.totalLabels * 1.5 / 60) })}</div>
                    </div>
                  )}
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button
                  onClick={() => setConfirmModal(null)}
                  className="px-4 py-2 rounded-xl bg-white/5 hover:bg-white/10 text-gray-300 text-sm font-semibold"
                  data-testid="batch-confirm-cancel"
                >
                  {t('labels.cancel')}
                </button>
                <button
                  onClick={confirmModalAccept}
                  className={`px-4 py-2 rounded-xl text-sm font-semibold ${
                    confirmModal.totalLabels > 5000
                      ? 'bg-red-500/20 text-red-300 hover:bg-red-500/30 border border-red-500/30'
                      : 'bg-amber-500/20 text-amber-300 hover:bg-amber-500/30 border border-amber-500/30'
                  }`}
                  data-testid="batch-confirm-accept"
                >
                  {confirmModal.totalLabels > 5000 ? t('labels.confirmAnyway') : t('labels.continue')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default EtiquettesPage;
